/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/order/api';
import { OrderDataModel } from '@/order/data/models';
import { transformPlaceNegotiableQuoteOrder } from '@/order/data/transforms';
import { publishPlaceOrderEvent } from '@/order/lib/acdl';
import { handleFetchError } from '@/order/lib/fetch-error';
import { handleNetworkError } from '@/order/lib/network-error';
  import { handlePlaceOrderError } from '@/order/lib/place-order-error';
import { PlaceNegotiableQuoteOrderResponse } from '@/order/types/index';
import { events } from '@adobe-commerce/event-bus';
import { PLACE_NEGOTIABLE_QUOTE_ORDER_MUTATION } from './graphql/placeNegotiableQuoteOrderMutation.graphql';
import { setReCaptchaToken } from '@/order/lib/setReCaptchaToken';

export const placeNegotiableQuoteOrder = async (
  quoteUid: string
): Promise<OrderDataModel | null | undefined> => {
  if (!quoteUid) throw new Error('No quote UID found');
  await setReCaptchaToken();

  return fetchGraphQl(PLACE_NEGOTIABLE_QUOTE_ORDER_MUTATION, {
    method: 'POST',
    variables: { quoteUid },
  })
    .then((response: PlaceNegotiableQuoteOrderResponse) => {
      if (response.errors?.length) {
        handleFetchError(response.errors);
      }

      if (response.data?.placeNegotiableQuoteOrderV2?.errors?.length) {
        handlePlaceOrderError(response.data.placeNegotiableQuoteOrderV2?.errors);
      }

      const payload = transformPlaceNegotiableQuoteOrder(response);

      if (payload) {
        events.emit('order/placed', payload);
        publishPlaceOrderEvent(quoteUid, payload);
      }

      return payload;
    })
    .catch(handleNetworkError);
};
